import { jwtDecode } from 'jwt-decode';
import { UserRoles } from '../../utils/userRoles';

export interface Roles {
  isSuperAdmin: boolean;
  restaurantManager: RestaurantManagerRole[];
  kitchenStaff: KitchenStaffRole[];
  restaurantWaiter: RestaurantWaiterRole[];
}

export interface RestaurantManagerRole {
  restaurantId: string;
}

export interface KitchenStaffRole {
  restaurantId: string;
  kitchenIds: string[];
}

export interface RestaurantWaiterRole {
  restaurantId: string;
}

export function getUserRoles(roles?: Roles): UserRoles[] {
  if (!roles) {
    return [];
  }

  const userRoles: UserRoles[] = [];

  if (roles.restaurantManager?.length > 0) {
    userRoles.push(UserRoles.RestaurantManager);
  }
  if (roles.kitchenStaff?.length > 0) {
    userRoles.push(UserRoles.KitchenStaff);
  }
  if (roles.restaurantWaiter?.length > 0) {
    userRoles.push(UserRoles.RestaurantWaiter);
  }

  if (roles.isSuperAdmin) {
    userRoles.push(UserRoles.SuperAdmin);
  }

  return userRoles;
}

export const getRolesFromJwt = (token: string): Roles => {
  interface DecodedToken {
    [key: string]: string | string[] | boolean | undefined;
    SuperAdmin?: boolean;
    RestaurantManager?: string | string[];
    KitchenStaff?: string | string[];
    RestaurantWaiter?: string | string[];
  }
  const decodedToken: DecodedToken = jwtDecode(token);
  const roles: Roles = {
    isSuperAdmin: false,
    restaurantManager: [],
    kitchenStaff: [],
    restaurantWaiter: [],
  };

  // Check for SuperAdmin role
  if (decodedToken['SuperAdmin']) {
    roles.isSuperAdmin = true;
  }

  // Check for RestaurantManager (RestaurantManager) roles
  if (decodedToken['RestaurantManager']) {
    const restaurantAdminIds = Array.isArray(decodedToken['RestaurantManager'])
      ? decodedToken['RestaurantManager']
      : [decodedToken['RestaurantManager']];
    roles.restaurantManager = restaurantAdminIds.map((id: string) => ({
      restaurantId: id,
    }));
  }

  // Check for KitchenStaff roles
  if (decodedToken['KitchenStaff']) {
    const kitchenStaffClaims = Array.isArray(decodedToken['KitchenStaff'])
      ? decodedToken['KitchenStaff']
      : [decodedToken['KitchenStaff']];
    roles.kitchenStaff = kitchenStaffClaims.map((claimString: string) => {
      const parts = claimString.split(':');
      const restaurantId = parts[0];
      const kitchenIds =
        parts.length > 1 && parts[1] ? parts[1].split(',') : [];
      return { restaurantId, kitchenIds };
    });
  }

  // Check for RestaurantWaiter roles
  if (decodedToken['RestaurantWaiter']) {
    const restaurantWaiterIds = Array.isArray(decodedToken['RestaurantWaiter'])
      ? decodedToken['RestaurantWaiter']
      : [decodedToken['RestaurantWaiter']];
    roles.restaurantWaiter = restaurantWaiterIds.map((id: string) => ({
      restaurantId: id,
    }));
  }

  return roles;
};
